import type { WhiteboardCardEvent } from "@/components/whiteboard/types";
import {
  type Spec,
  createSpecStreamCompiler,
  parseElement,
} from "@chickytutor/whiteboard-stream-renderer";
import { z } from "zod";
import { resetWhiteboardRenderState } from "../boardState";
import { type Activity, ActivitySchema } from "../generated/contract";
import { checkAnswer } from "../generated/state";
import { type WhiteboardSpec, type WhiteboardUpdateOp, normalizeAnswer } from "../spec";
import { validateComposition } from "./composition";
import { ObjectFramer } from "./framing";
import { type NativeExercise, annotateNative, parseNative } from "./native";
import { emphasisTargets, lastSequenceEnd } from "./spoken";

export const STREAM_TOPIC = "chickytutor.whiteboard.stream.v1";
export const STREAM_EVENTS = "chickytutor.whiteboard.stream.events.v1";
export const STREAM_CAPABILITY = "chickytutor.whiteboard.stream";
/** Separate attribute so older workers keep streaming to this client unchanged. */
export const STREAM_REVIEW_CAPABILITY = "chickytutor.whiteboard.review";
export const STREAM_KEEP_CAPABILITY = "chickytutor.whiteboard.keep";
export const LIMITS = {
  bytes: 96 * 1024,
  line: 8192,
  elements: 32,
  depth: 8,
  activities: 3,
  deadline: 20000,
};
const identifier = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,47}$/)
  .refine((value) => !["constructor", "prototype", "__proto__"].includes(value));
const activityRecord = z.object({ type: z.literal("activity"), activity: ActivitySchema }).strict();
const patchRecord = z
  .object({ op: z.literal("add"), path: z.string().max(100), value: z.unknown() })
  .strict();
export type StreamPhase = "preparing" | "complete" | "failed" | "cancelled";
export type StreamSnapshot = {
  fault?: string;
  spec: Spec;
  activities: Record<string, Activity>;
  exercises: Record<string, NativeExercise>;
  annotations: Record<string, number>;
  answers: Record<string, string>;
  results: Record<string, "correct" | "incorrect" | "pending" | "completed" | "read" | "observed">;
  phase: StreamPhase;
  ready: string[];
  /** Native tasks shown before their teaching review: visible, locked, never graded. */
  reviewing: string[];
  /** Native tasks the review could not verify: kept visible, never interactive. */
  withdrawn: string[];
  revealed: Record<string, boolean>;
  bytes: number;
  timing: { useful?: number; actionable?: number; complete?: number };
  /** A visible choice the tutor has just named aloud; pulses briefly, never persisted. */
  emphasis?: { id: string; target: string };
};
/** Only the tutor's most recent words point at a choice. */
const EMPHASIS_WINDOW_WORDS = 14;
const EMPHASIS_MS = 1600;
export type ReviewVerdict = "valid" | "repair" | "reject";
const emptySpec = (): Spec => ({
  root: "board",
  elements: { board: { type: "Stack", props: {}, children: [] } },
});

/** Application-owned state and validation surround the library's patch compiler. */
export class StreamController {
  onCheck?: (event: {
    activity: string;
    answer: string;
    result: string;
    resolved: boolean;
  }) => void;
  onNativeEvent?: (id: string, event: WhiteboardCardEvent) => void;
  onDisplayed?: () => void;
  onHidden?: () => void;
  onClosed?: () => void;
  private compiler = createSpecStreamCompiler<Spec>(emptySpec());
  private decoder = new TextDecoder("utf-8", { fatal: true });
  private framer = new ObjectFramer(LIMITS.line);
  private listeners = new Set<() => void>();
  private definitions: Record<string, Activity> = {};
  private native: Record<string, NativeExercise> = {};
  private reading = false;
  private ended = false;
  private started: number;
  private watchdog: ReturnType<typeof setTimeout>;
  private repaired = false;
  private keepsUsable = false;
  private reviewing = new Set<string>();
  private withdrawn = new Set<string>();
  private emphasisSource = "";
  private emphasisEnd = 0;
  private emphasisTimer?: ReturnType<typeof setTimeout>;
  private lastRecord = "";
  private rejection = "";
  private visible = false;
  snapshot: StreamSnapshot;
  constructor(
    readonly mode: "progressive" | "buffered" = "progressive",
    private now = () => performance.now(),
    readonly composition = false
  ) {
    this.started = now();
    this.snapshot = {
      spec: emptySpec(),
      activities: {},
      exercises: {},
      annotations: {},
      answers: {},
      results: {},
      phase: "preparing",
      ready: [],
      reviewing: [],
      withdrawn: [],
      revealed: {},
      bytes: 0,
      timing: {},
    };
    this.watchdog = setTimeout(() => this.fail(), LIMITS.deadline);
  }
  /** Interactive tasks: ready and not waiting for, or withdrawn by, the review. */
  get usable() {
    const { ready, reviewing, withdrawn } = this.snapshot;
    return ready.filter((id) => !reviewing.includes(id) && !withdrawn.includes(id));
  }
  /**
   * Tasks that decide whether the board is resolved: every ready task of a complete board, or the usable
   * tasks a failed board keeps, but only when the worker's `failed` frame said it keeps them
   * (docs/plans/active/whiteboard-board-recovery-2026-10-13.md), so both sides record the same outcome.
   * Empty while the board cannot be resolved.
   */
  get resolution(): string[] {
    if (this.snapshot.phase === "complete") return this.snapshot.ready;
    if (this.snapshot.phase === "failed" && this.keepsUsable) return this.usable;
    return [];
  }
  /** The teaching review is invisible: a task under (or failed by) it is not shown. */
  private held(element: { type: string; props: Record<string, unknown> }) {
    const { reviewing, withdrawn } = this.snapshot;
    const id = String(element.props.exerciseId);
    return element.type === "Exercise" && (reviewing.includes(id) || withdrawn.includes(id));
  }
  /** True once the board has something the learner can see. */
  get hasVisibleContent() {
    const { spec } = this.snapshot;
    return Object.entries(spec.elements).some(
      ([id, element]) => id !== spec.root && element.type !== "Stack" && !this.held(element)
    );
  }
  /**
   * Pulse the choice the tutor is naming, from its audio-paced transcript. Only a
   * new occurrence in the latest words counts, so one mention pulses once.
   */
  emphasize(tutorText: string) {
    if (this.snapshot.phase === "cancelled" || !this.visible) return;
    const words = normalizeAnswer(tutorText).split(" ").filter(Boolean);
    if (!tutorText.startsWith(this.emphasisSource)) this.emphasisEnd = 0;
    this.emphasisSource = tutorText;
    const windowStart = Math.max(0, words.length - EMPHASIS_WINDOW_WORDS);
    let best: { id: string; target: string; end: number } | undefined;
    for (const id of this.usable) {
      for (const target of emphasisTargets(this.native[id]?.spec)) {
        const tokens = normalizeAnswer(target).split(" ").filter(Boolean);
        if (!tokens.length || (tokens.length < 2 && target.trim().length < 5)) continue;
        const end = lastSequenceEnd(words, tokens);
        if (
          end - tokens.length >= windowStart &&
          end > this.emphasisEnd &&
          (!best || end > best.end)
        )
          best = { id, target, end };
      }
    }
    if (!best) return;
    this.emphasisEnd = best.end;
    clearTimeout(this.emphasisTimer);
    this.update({ emphasis: { id: best.id, target: best.target } });
    this.emphasisTimer = setTimeout(() => this.update({ emphasis: undefined }), EMPHASIS_MS);
  }
  /** Called by the transport before the matching record is pushed. */
  expectReview(id: string) {
    if (identifier.safeParse(id).success) this.reviewing.add(id);
  }
  /** Apply the teaching-review verdict for a task published before its review. */
  review(id: string, verdict: ReviewVerdict, record?: string) {
    const unit = this.native[id];
    if (!unit || !this.reviewing.has(id)) return false;
    let outcome = verdict;
    const previous = this.native;
    if (verdict === "repair") {
      try {
        const parsed = z
          .object({ type: z.literal("exercise"), spec: z.unknown() })
          .strict()
          .parse(JSON.parse(record ?? ""));
        const spec = parseNative(parsed.spec);
        if (spec.id !== id || spec.kind !== unit.spec.kind) throw new Error("Exercise identity");
        this.native = { ...this.native, [id]: { ...unit, spec } };
        this.project();
      } catch {
        this.native = previous;
        outcome = "reject";
      }
    }
    this.reviewing.delete(id);
    if (outcome === "reject") this.withdrawn.add(id);
    this.update({
      exercises: { ...this.native },
      reviewing: [...this.reviewing],
      withdrawn: [...this.withdrawn],
    });
    return outcome !== "reject";
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.snapshot;
  get rejectedRecord() {
    return this.snapshot.phase === "failed" ? this.rejection : "";
  }
  private update(part: Partial<StreamSnapshot>) {
    this.snapshot = { ...this.snapshot, ...part };
    this.listeners.forEach((listener) => listener());
  }
  push(bytes: Uint8Array) {
    if (this.snapshot.phase !== "preparing") return;
    try {
      const count = this.snapshot.bytes + bytes.length;
      if (count > LIMITS.bytes || this.now() - this.started > LIMITS.deadline)
        throw new Error("Budget");
      this.snapshot = { ...this.snapshot, bytes: count };
      for (const line of this.framer.feed(this.decoder.decode(bytes, { stream: true }))) {
        this.lastRecord = line;
        this.accept(line);
      }
      if (this.mode === "progressive") this.publish();
    } catch (error) {
      this.rejection = this.lastRecord;
      if (this.mode === "progressive") this.publish();
      this.fail(
        error instanceof SyntaxError
          ? "Malformed JSON"
          : error instanceof z.ZodError
            ? "Activity schema"
            : error instanceof Error && error.message.length < 80
              ? error.message
              : "Element schema"
      );
    }
  }
  private accept(line: string) {
    if (new TextEncoder().encode(line).length > LIMITS.line) throw new Error("Line budget");
    const record: unknown = JSON.parse(line);
    if (this.reading || this.ended) throw new Error("Reading board is complete");
    if (record && typeof record === "object" && "type" in record && record.type === "board_end") {
      z.object({ type: z.literal("board_end") })
        .strict()
        .parse(record);
      if (!this.composition) throw new Error("Record type");
      this.project(true);
      this.ended = true;
      return;
    }
    if (
      record &&
      typeof record === "object" &&
      "type" in record &&
      (record.type === "exercise" || record.type === "reading_end")
    ) {
      let spec: WhiteboardSpec;
      const readingEnd = record.type === "reading_end";
      const raw = this.compiler.getResult();
      if (readingEnd) {
        const end = z
          .object({ type: z.literal("reading_end"), id: identifier })
          .strict()
          .parse(record);
        if (Object.keys(this.definitions).length || Object.keys(this.native).length)
          throw new Error("Reading-only content");
        const content = (raw.elements.board.children ?? []).map((id) => raw.elements[id]);
        if (content.some((e) => !e || !["Heading", "Text"].includes(e.type)))
          throw new Error("Reading-only content");
        spec = parseNative({
          kind: "reading_story",
          id: end.id,
          title: content.find((e) => e.type === "Heading")?.props.text,
          story: content
            .filter((e) => e.type === "Text")
            .map((e) => e.props.text)
            .join("\n\n"),
        });
      } else {
        const parsed = z
          .object({ type: z.literal("exercise"), spec: z.unknown() })
          .strict()
          .parse(record);
        spec = parseNative(parsed.spec);
        if (spec.kind === "reading_story") throw new Error("Stream paragraphs then reading_end");
      }
      identifier.parse(spec.id);
      if (this.composition)
        validateComposition(raw, {
          ...this.native,
          [spec.id]: { spec, readingEnd, renderState: resetWhiteboardRenderState() },
        });
      if (
        raw.elements[spec.id] ||
        Object.keys(raw.elements).length >= LIMITS.elements ||
        this.definitions[spec.id] ||
        Object.keys(this.native).length >= 6 ||
        (!raw.elements.board.children?.includes(spec.id) &&
          (raw.elements.board.children?.length ?? 0) >= 8)
      )
        throw new Error("Exercise identity");
      const previousNative = this.native;
      const previousSpec = JSON.stringify(raw);
      this.native = {
        ...this.native,
        [spec.id]: { spec, readingEnd, renderState: resetWhiteboardRenderState() },
      };
      this.compiler.push(
        JSON.stringify({
          op: "add",
          path: `/elements/${spec.id}`,
          value: { type: "Exercise", props: { exerciseId: spec.id }, children: [] },
        }) + "\n"
      );
      if (!raw.elements.board.children?.includes(spec.id))
        this.compiler.push(
          JSON.stringify({ op: "add", path: "/elements/board/children/-", value: spec.id }) + "\n"
        );
      try {
        this.project();
      } catch (error) {
        this.native = previousNative;
        this.compiler = createSpecStreamCompiler<Spec>(JSON.parse(previousSpec));
        throw error;
      }
      this.reading = readingEnd;
      if (readingEnd) this.ended = true;
      return;
    }
    if (record && typeof record === "object" && "type" in record) {
      if (this.composition) throw new Error("Record type");
      const { activity } = activityRecord.parse(record);
      if (
        activity.prompt.length > 240 ||
        activity.hint.length > 240 ||
        activity.explanation.length > 300 ||
        activity.options.length > 8 ||
        activity.options.some((value) => value.length > 100) ||
        activity.answers.length > 8 ||
        activity.answers.some((value) => value.length > 200)
      )
        throw new Error("Activity budget");
      identifier.parse(activity.id);
      if (!["text", "choice"].includes(activity.mode)) throw new Error("Unsupported activity");
      if (
        this.definitions[activity.id] ||
        Object.keys(this.definitions).length >= LIMITS.activities
      )
        throw new Error("Activity identity");
      const previous = this.definitions;
      this.definitions = { ...previous, [activity.id]: activity };
      try {
        this.project();
      } catch (error) {
        this.definitions = previous;
        throw error;
      }
      return;
    }
    const patch = patchRecord.parse(record);
    const raw = this.compiler.getResult();
    if (patch.path === "/elements/board/children/-") {
      const child = identifier.parse(patch.value);
      // Native controls attach themselves. The same attachment from the provider
      // changes neither the definition, position, answers nor learner state.
      if (this.native[child] && raw.elements.board.children?.includes(child)) return;
      if (
        child === "board" ||
        raw.elements.board.children?.includes(child) ||
        (raw.elements.board.children?.length ?? 0) >= 8
      )
        throw new Error("Root children");
    } else {
      const match = /^\/elements\/([a-zA-Z][a-zA-Z0-9_-]{0,47})$/.exec(patch.path);
      if (!match || match[1] === "board") throw new Error("Patch path");
      identifier.parse(match[1]);
      if (raw.elements[match[1]]) throw new Error("Committed element");
      if (!raw.elements[match[1]] && Object.keys(raw.elements).length >= LIMITS.elements)
        throw new Error("Element budget");
      const element = parseElement(patch.value);
      if (element.children?.includes(match[1])) throw new Error("Cycle");
      patch.value = element;
    }
    // The compiler only receives validated complete records, never untrusted paths.
    const previous = JSON.stringify(raw);
    this.compiler.push(JSON.stringify(patch) + "\n");
    try {
      this.project();
    } catch (error) {
      this.compiler = createSpecStreamCompiler<Spec>(JSON.parse(previous));
      throw error;
    }
  }
  private project(final = false) {
    const raw = this.compiler.getResult();
    if (this.composition) validateComposition(raw, this.native);
    const spec = emptySpec();
    const ready: string[] = [];
    const visited = new Set<string>();
    const visit = (id: string, path: Set<string>): string | null => {
      if (path.has(id) || path.size > LIMITS.depth || visited.has(id))
        throw new Error("Invalid tree");
      const element = raw.elements[id];
      if (!element) {
        if (final) throw new Error("Missing child");
        return null;
      }
      visited.add(id);
      if (element.type === "Exercise") {
        const exerciseId = String(element.props.exerciseId);
        if (!this.native[exerciseId] || ready.includes(exerciseId))
          throw new Error("Native exercise identity");
        ready.push(exerciseId);
      }
      if (element.type === "Activity") {
        const activityId = String(element.props.activityId);
        if (!this.definitions[activityId]) {
          if (final) throw new Error("Missing activity");
          return null;
        }
        if (ready.includes(activityId)) throw new Error("Repeated activity");
        ready.push(activityId);
      }
      const children = (element.children ?? []).flatMap((child) => {
        const accepted = visit(child, new Set([...path, id]));
        return accepted ? [accepted] : [];
      });
      if (id !== "board" && ["Panel", "Stack"].includes(element.type) && !children.length)
        return null;
      spec.elements[id] = { ...element, children };
      return id;
    };
    visit("board", new Set());
    if (final) {
      if (!ready.length) throw new Error("Missing task");
      if (ready.length !== Object.keys(this.definitions).length + Object.keys(this.native).length)
        throw new Error("Unreferenced activity");
      if (visited.size !== Object.keys(raw.elements).length)
        throw new Error("Unreferenced element");
    }
    return { spec, ready };
  }
  private publish(final = false) {
    const { spec, ready } = this.project(final);
    const reviewing = [...this.reviewing];
    if (
      JSON.stringify([spec, ready, this.definitions, this.native, reviewing]) ===
      JSON.stringify([
        this.snapshot.spec,
        this.snapshot.ready,
        this.snapshot.activities,
        this.snapshot.exercises,
        this.snapshot.reviewing,
      ])
    )
      return;
    // A task awaiting review arrives locked in the same update that adds it.
    this.update({
      spec,
      ready,
      reviewing,
      activities: { ...this.definitions },
      exercises: { ...this.native },
    });
  }
  get hasCompleteBoard() {
    if (this.composition && !this.ended) return false;
    try {
      this.project(true);
      return true;
    } catch {
      return false;
    }
  }
  finish() {
    if (this.snapshot.phase !== "preparing") return;
    try {
      for (const line of this.framer.feed(this.decoder.decode())) this.accept(line);
      this.framer.finish();
      if (this.composition && !this.ended) throw new Error("Incomplete board");
      this.publish(true);
      clearTimeout(this.watchdog);
      this.update({ phase: "complete" });
    } catch (error) {
      this.fail(
        error instanceof Error && error.message.length < 80 ? error.message : "Incomplete board"
      );
    }
  }
  /** `keepsUsable` comes only from the worker's `failed` frame; local failures never resolve a board. */
  fail(fault = "Stream unavailable", keepsUsable = false) {
    clearTimeout(this.watchdog);
    this.keepsUsable = keepsUsable;
    this.update({ phase: "failed", fault });
  }
  cancel() {
    clearTimeout(this.watchdog);
    this.update({ phase: "cancelled" });
  }
  dismiss() {
    this.cancel();
    this.onClosed?.();
  }
  /** A developer/provider may correct one rejected record; accepted tasks stay frozen. */
  repair(line: string) {
    if (
      this.snapshot.phase !== "failed" ||
      this.repaired ||
      !this.rejection ||
      this.now() - this.started >= LIMITS.deadline
    )
      return false;
    this.repaired = true;
    try {
      this.accept(line);
      this.update({ phase: "preparing", fault: undefined });
      this.watchdog = setTimeout(
        () => this.fail(),
        Math.max(1, LIMITS.deadline - (this.now() - this.started))
      );
      this.push(new Uint8Array());
      return this.getSnapshot().phase === "preparing";
    } catch {
      this.fail();
      return false;
    }
  }
  answer(id: string, value: string) {
    if (
      !this.snapshot.ready.includes(id) ||
      value.length > 2000 ||
      this.snapshot.results[id] === "correct"
    )
      return;
    this.update({
      answers: { ...this.snapshot.answers, [id]: value },
      results: { ...this.snapshot.results, [id]: "pending" },
    });
  }
  check(id: string) {
    const answer = this.snapshot.answers[id];
    if (
      !this.snapshot.ready.includes(id) ||
      !answer?.trim() ||
      this.snapshot.results[id] === "correct"
    )
      return;
    this.update({
      results: { ...this.snapshot.results, [id]: checkAnswer(this.definitions[id], answer) },
    });
    this.onCheck?.({
      activity: id,
      answer,
      result: this.snapshot.results[id],
      resolved:
        this.resolution.length > 0 &&
        this.resolution.every((key) => this.snapshot.results[key] === "correct"),
    });
  }
  nativeEvent(id: string, event: WhiteboardCardEvent) {
    if (!this.usable.includes(id) || this.snapshot.phase === "cancelled") return;
    if (event.event === "reading_complete")
      this.update({ results: { ...this.snapshot.results, [id]: "read" } });
    else if (["answered", "completed"].includes(event.event))
      this.update({
        results: {
          ...this.snapshot.results,
          [id]:
            event.correct === true
              ? "correct"
              : event.correct === false
                ? "incorrect"
                : "completed",
        },
      });
    this.onNativeEvent?.(id, event);
  }
  annotate(id: string, operations: WhiteboardUpdateOp[], revision: number) {
    if (
      !this.native[id] ||
      this.snapshot.phase === "cancelled" ||
      revision !== (this.snapshot.annotations[id] ?? 0) + 1
    )
      return;
    try {
      this.native = { ...this.native, [id]: annotateNative(this.native[id], operations) };
      this.update({
        exercises: { ...this.native },
        annotations: { ...this.snapshot.annotations, [id]: revision },
      });
    } catch {
      this.fail("Board update unavailable");
    }
  }
  mounted() {
    this.visible = true;
    const { spec, ready, phase } = this.snapshot;
    const timing = { ...this.snapshot.timing };
    const elapsed = Math.round(this.now() - this.started);
    if (
      timing.useful === undefined &&
      Object.values(spec.elements).some(
        (element) =>
          ["Text", "Table", "Activity", "Exercise"].includes(element.type) && !this.held(element)
      )
    )
      timing.useful = elapsed;
    if (timing.actionable === undefined && ready.length && this.usable.length)
      timing.actionable = elapsed;
    if (timing.complete === undefined && phase === "complete") timing.complete = elapsed;
    if (JSON.stringify(timing) !== JSON.stringify(this.snapshot.timing)) this.update({ timing });
    this.onDisplayed?.();
  }
  disclose(label: string, text: string, open: boolean) {
    const entry = Object.entries(this.snapshot.spec.elements).find(
      ([, item]) => item.type === "Reveal" && item.props.label === label && item.props.text === text
    );
    if (
      !entry ||
      this.snapshot.phase === "cancelled" ||
      Boolean(this.snapshot.revealed[entry[0]]) === open
    )
      return;
    this.update({ revealed: { ...this.snapshot.revealed, [entry[0]]: open } });
    if (this.visible) this.onDisplayed?.();
  }
  hidden() {
    if (!this.visible) return;
    this.visible = false;
    this.onHidden?.();
  }
  close() {
    clearTimeout(this.watchdog);
    clearTimeout(this.emphasisTimer);
    this.listeners.clear();
  }
}
